'use client';

// ============================================================================
// EspacePwaInstall : bannière d'installation PWA pour les HUBS D'ESPACE
// ============================================================================
// D'après mockups/connexion-espaces.html, section « Installation ». Absente
// avant le 20/09/2026 : seul le portail salarié proposait l'installation
// (docs/ecarts-maquettes-clients-2026-09-20.md, §4, « absente pour les
// espaces »). Un technicien qui remplit des bons d'intervention toute la
// journée n'avait aucun moyen d'épingler l'espace sur son écran d'accueil.
//
// REFAIT LE 30/09/2026 (demande du dirigeant) :
//  - le texte suit l'APPAREIL (ordinateur, téléphone, tablette) : il disait
//    « sur ce téléphone » à quelqu'un qui installait sur son ordinateur ;
//  - un espace DÉJÀ INSTALLÉ ne se repropose plus. Quatre preuves, mémorisées
//    par origine : l'application ouverte en fenêtre installée (standalone),
//    l'événement « appinstalled », navigator.getInstalledRelatedApps() quand le
//    navigateur le connaît (le manifeste déclare son application), et le bouton
//    « C'est déjà fait » ;
//  - « Plus tard » endort le bandeau N jours (réglage du COMPTE, Réglages >
//    Univers, 14 par défaut), puis il revient seul ;
//  - quand le navigateur ne fournit pas son invite d'installation (Safari, ou
//    Chrome qui ne la donne pas), un COURT GUIDE dit où cliquer, au lieu de ne
//    rien montrer. Il obéit au même sommeil et à « C'est déjà fait » : il ne doit
//    jamais revenir sans cesse chez quelqu'un qui a déjà installé ;
//  - au téléphone, il se pose AU-DESSUS de la bulle du chat (--bulle-reserve).
//
// Couleur de MARQUE, jamais violet en dur : chaque espace a son accent, imposé
// par verifier-violet.mjs. Un seul montage (chrome-applicatif, écran de
// connexion) : tout espace présent ou futur l'a sans rien écrire.
//
// Ce composant NE CRÉE PAS un second capteur `beforeinstallprompt` : il lit
// `window.__vyndraBIP`, posé par le script inline de app/layout.tsx (racine).
// ============================================================================

import { useEffect, useState } from 'react';
import { X, DownloadSimple as Download, DeviceMobile as Smartphone } from '@phosphor-icons/react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** Repli quand le réglage du compte n'a pas pu être lu. */
const SOMMEIL_PAR_DEFAUT_JOURS = 14;

/** Délai avant de conclure que le navigateur ne donnera pas son invite. */
const ATTENTE_INVITE_MS = 4000;

type Appareil = 'telephone' | 'tablette' | 'ordinateur';

/**
 * Ce que le bandeau montre :
 *  - « invite » : le navigateur fournit l'installation, un bouton suffit ;
 *  - « ios » : Safari, le guide « Partager, puis Sur l'écran d'accueil » ;
 *  - « mac » : Safari sur Mac, « Fichier, puis Ajouter au Dock » ;
 *  - « menu » : Chrome, Edge, Samsung, sans invite, le guide du menu du navigateur.
 */
type Mode = 'invite' | 'ios' | 'mac' | 'menu';

function cleSommeil(slug: string): string {
  return `vyndra_espace_pwa_dismissed_at_${slug}`;
}
function cleInstalle(slug: string): string {
  return `vyndra_espace_installe_${slug}`;
}

function lire(cle: string): string | null {
  try { return localStorage.getItem(cle); } catch { return null; }
}
function ecrire(cle: string, valeur: string) {
  try { localStorage.setItem(cle, valeur); } catch { /* stockage refusé : le bandeau reviendra, rien de grave */ }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: window-controls-overlay)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true;
}

/** iPhone, iPod, et iPad, y compris l'iPad qui se dit « Macintosh » avec un écran tactile. */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function detecterAppareil(): Appareil {
  const ua = navigator.userAgent;
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'tablette';
  if (/iPhone|iPod/.test(ua)) return 'telephone';
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'telephone' : 'tablette';
  if ((navigator as { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile) return 'telephone';
  return 'ordinateur';
}

/**
 * Le guide à montrer quand le navigateur ne fournit pas son invite, ou null quand
 * il ne sait pas installer un site (Firefox sur ordinateur) : un guide vers une
 * fonction absente serait un conseil faux.
 */
function modeSansInvite(appareil: Appareil): Mode | null {
  const ua = navigator.userAgent;
  if (isIos()) return 'ios';
  const chromium = /Chrome|Chromium|Edg\/|SamsungBrowser|OPR\//.test(ua);
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !chromium) return 'mac';
  if (/Firefox|FxiOS/.test(ua)) return appareil === 'ordinateur' ? null : 'menu';
  return chromium ? 'menu' : null;
}

/** L'espace est-il connu comme installé sur cet appareil (manifeste déclaré) ? */
async function installeSelonLeNavigateur(): Promise<boolean> {
  const nav = navigator as { getInstalledRelatedApps?: () => Promise<unknown[]> };
  if (typeof nav.getInstalledRelatedApps !== 'function') return false;
  try {
    const applis = await nav.getInstalledRelatedApps();
    return Array.isArray(applis) && applis.length > 0;
  } catch {
    return false;
  }
}

// Lu depuis window.__vyndraBIP, posé par le script inline de app/layout.tsx.
function readGlobalBIP(): BeforeInstallPromptEvent | null {
  if (typeof window === 'undefined') return null;
  return ((window as unknown as { __vyndraBIP?: BeforeInstallPromptEvent }).__vyndraBIP) ?? null;
}
function clearGlobalBIP() {
  if (typeof window !== 'undefined') {
    (window as unknown as { __vyndraBIP?: BeforeInstallPromptEvent | null }).__vyndraBIP = null;
  }
}

/** Réutilisable par un futur bouton « Installer » dans les réglages de l'espace. */
export function getEspaceInstallPrompt(): BeforeInstallPromptEvent | null {
  return readGlobalBIP();
}

export async function triggerEspaceInstallPrompt(): Promise<boolean> {
  const ev = getEspaceInstallPrompt();
  if (!ev) return false;
  try {
    await ev.prompt();
    const resultat = await ev.userChoice;
    clearGlobalBIP();
    return resultat.outcome === 'accepted';
  } catch {
    return false;
  }
}

/** Le texte selon l'appareil, pour le bandeau avec invite. */
function texteInvite(appareil: Appareil, nom: string): string {
  switch (appareil) {
    case 'ordinateur':
      return `Installez ${nom} sur cet ordinateur : une icône dans la barre des tâches et une fenêtre à ses couleurs, séparée des autres espaces.`;
    case 'tablette':
      return `Installez ${nom} sur cette tablette : une icône sur l'écran d'accueil, à ses couleurs, séparée des autres espaces.`;
    default:
      return `Installez ${nom} sur ce téléphone : une icône sur l'écran d'accueil et un écran de démarrage à ses couleurs, séparés des autres espaces.`;
  }
}

interface Props {
  /** Slug de l'espace : sert de clé de mémoire (sommeil, installé). */
  slug: string;
  /** Nom affiché de l'espace ; à défaut, celui que la résolution publique renvoie. */
  nomEspace?: string | null;
}

export function EspacePwaInstall({ slug, nomEspace }: Props) {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);
  const [appareil, setAppareil] = useState<Appareil>('ordinateur');
  const [nomServeur, setNomServeur] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !slug) return;
    let annule = false;
    const minuteries: ReturnType<typeof setTimeout>[] = [];

    const marquerInstalle = () => {
      ecrire(cleInstalle(slug), '1');
      clearGlobalBIP();
      setVisible(false);
    };

    // 1. Ouvert DANS l'application installée : c'est la preuve même.
    if (isStandalone()) { ecrire(cleInstalle(slug), '1'); return; }
    // 2. Déjà su installé sur cet appareil.
    if (lire(cleInstalle(slug)) === '1') return;

    const appareilDetecte = detecterAppareil();
    setAppareil(appareilDetecte);

    const onInstalle = () => marquerInstalle();
    window.addEventListener('appinstalled', onInstalle);
    const onInvite = (e: Event) => {
      e.preventDefault();
      if (annule) return;
      setPromptEvent(e as BeforeInstallPromptEvent);
      setMode('invite');
    };
    window.addEventListener('beforeinstallprompt', onInvite);

    void (async () => {
      // 3. Le réglage du compte (sommeil de « Plus tard ») et le nom de marque.
      let sommeilJours = SOMMEIL_PAR_DEFAUT_JOURS;
      try {
        const r = await fetch('/api/espace/installation', { cache: 'no-store' });
        if (r.ok) {
          const data = (await r.json()) as { rappelJours?: number; nom?: string | null };
          if (typeof data.rappelJours === 'number' && data.rappelJours >= 1) sommeilJours = data.rappelJours;
          if (!annule && data.nom) setNomServeur(data.nom);
        }
      } catch { /* repli : 14 jours */ }
      if (annule) return;
      const endormi = Number.parseInt(lire(cleSommeil(slug)) ?? '', 10);
      if (Number.isFinite(endormi) && Date.now() - endormi < sommeilJours * 86_400_000) return;

      // 4. Le navigateur sait-il déjà que l'espace est installé ?
      if (await installeSelonLeNavigateur()) { if (!annule) marquerInstalle(); return; }
      if (annule) return;

      // 5. L'invite du navigateur : déjà captée par le script du layout, ou à venir.
      const dejaCapte = readGlobalBIP();
      if (dejaCapte) {
        setPromptEvent(dejaCapte);
        setMode('invite');
        minuteries.push(setTimeout(() => { if (!annule) setVisible(true); }, 2000));
        return;
      }
      // 6. Sans invite au bout de quelques secondes : le guide, s'il y en a un.
      minuteries.push(setTimeout(() => {
        if (annule) return;
        setMode((actuel) => actuel ?? modeSansInvite(appareilDetecte));
        setVisible(true);
      }, ATTENTE_INVITE_MS));
    })();

    return () => {
      annule = true;
      minuteries.forEach(clearTimeout);
      window.removeEventListener('appinstalled', onInstalle);
      window.removeEventListener('beforeinstallprompt', onInvite);
    };
  }, [slug]);

  const plusTard = () => {
    setVisible(false);
    ecrire(cleSommeil(slug), String(Date.now()));
  };

  const dejaFait = () => {
    setVisible(false);
    ecrire(cleInstalle(slug), '1');
  };

  const installer = async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const resultat = await promptEvent.userChoice;
    clearGlobalBIP();
    if (resultat.outcome === 'accepted') dejaFait();
    else plusTard();
  };

  if (!visible || mode === null) return null;
  if (mode === 'invite' && !promptEvent) return null;

  const nom = nomEspace?.trim() || nomServeur?.trim() || 'cet espace';
  const titre = mode === 'invite' ? `Garde ${nom} à portée de main` : `Installer ${nom}`;
  const Icone = appareil === 'ordinateur' ? Download : Smartphone;

  return (
    <div
      role="dialog"
      aria-label={titre}
      className="fixed left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 rounded-2xl border border-[#2A2D35] bg-gradient-to-br from-[#15171A] via-[#16181C] to-[#1A1D22] shadow-2xl"
      // Au-dessus de la bulle du chat (même réserve que le contenu de l'écran) ; là où
      // la bulle n'existe pas (réserve négative), une simple marge.
      style={{
        bottom: 'max(calc(env(safe-area-inset-bottom, 0px) + 16px), calc(env(safe-area-inset-bottom, 0px) + var(--bulle-reserve, 84px)))',
        animation: 'espace-pwa-slide-up 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
      }}
    >
      <style>{'@keyframes espace-pwa-slide-up { from { transform: translateY(120%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }'}</style>
      <button
        onClick={plusTard}
        aria-label="Plus tard"
        className="absolute top-1 right-1 w-11 h-11 rounded-md flex items-center justify-center text-[#5A5F6A] hover:text-[#E8E9EB] hover:bg-[#1F2127] transition-colors"
      >
        <X size={14} />
      </button>
      <div className="p-4 pr-12">
        <div className="text-[13.5px] font-semibold text-[#E8E9EB] leading-tight flex items-center gap-2">
          <Icone size={14} className="text-[var(--acc-l,var(--acc,#22D3EE))]" /> {titre}
        </div>
        <p className="text-[12px] text-[#8A8F98] mt-1.5 leading-relaxed" style={{ textWrap: 'pretty' }}>
          {mode === 'invite' && texteInvite(appareil, nom)}
          {mode === 'ios' && (
            <>Appuyez sur <strong className="text-[#E8E9EB]">Partager</strong> en bas de Safari, puis sur{' '}
            <strong className="text-[#E8E9EB]">« Sur l&apos;écran d&apos;accueil »</strong> : une icône à ses couleurs,
            séparée des autres espaces.</>
          )}
          {mode === 'mac' && (
            <>Dans Safari, ouvrez le menu <strong className="text-[#E8E9EB]">Fichier</strong>, puis{' '}
            <strong className="text-[#E8E9EB]">« Ajouter au Dock »</strong> : {nom} s&apos;ouvrira dans sa propre fenêtre.</>
          )}
          {mode === 'menu' && (appareil === 'ordinateur' ? (
            <>Cliquez l&apos;icône d&apos;installation au bout de la barre d&apos;adresse, ou ouvrez le menu du
            navigateur puis <strong className="text-[#E8E9EB]">« Installer »</strong> : une icône dans la barre des
            tâches, à ses couleurs.</>
          ) : (
            <>Ouvrez le menu du navigateur (les trois points), puis{' '}
            <strong className="text-[#E8E9EB]">« Installer l&apos;application »</strong> ou{' '}
            <strong className="text-[#E8E9EB]">« Ajouter à l&apos;écran d&apos;accueil »</strong>.</>
          ))}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {mode === 'invite' ? (
            <button
              onClick={() => void installer()}
              className="min-h-[44px] text-[12.5px] px-3.5 py-2 rounded-md font-semibold text-[var(--acc-on,#fff)] inline-flex items-center gap-1.5 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
              style={{ background: 'linear-gradient(180deg, var(--acc,#22D3EE), var(--acc-d,var(--acc,#0E7490)))' }}
            >
              <Download size={12} /> Installer
            </button>
          ) : (
            <button
              onClick={dejaFait}
              className="min-h-[44px] text-[12.5px] px-3.5 py-2 rounded-md font-semibold text-[#E8E9EB] border border-[#2A2D35] hover:bg-[#1F2127] transition-colors"
            >
              C&apos;est déjà fait
            </button>
          )}
          <button
            onClick={plusTard}
            className="min-h-[44px] text-[12px] px-2.5 py-2 rounded-md text-[#8A8F98] hover:text-[#E8E9EB] hover:bg-[#1F2127] transition-colors"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
}
